import { useEffect, useMemo, useState, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { BotTradeRow, BotTradeRowChain, BotTradeRowTape } from "@/lib/scalperPaperEngine";
import { formatMcUsdBookDetail, formatSol } from "@/lib/formatUsd";
import { useApp } from "@/context/AppCtx";
import { normalizeCoinImageUri } from "@/lib/imageUtils";
import { ChevronUp, ChevronDown } from "lucide-react";

const SOLSCAN_TX = "https://solscan.io/tx/";

/** Tape MC move entry→exit (same formula as `reduceScalperPaper`). */
function mcMovePct(entryUsd: number, exitUsd: number): number {
  if (!(entryUsd > 0) || !Number.isFinite(exitUsd)) return 0;
  return ((exitUsd - entryUsd) / entryUsd) * 100;
}

function formatAge(ts: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

function formatTime(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function exitLabel(r: BotTradeRow): string {
  return r.exitReason === "take_profit" ? "TP" : "Sell";
}

function SortableHeader({
  column,
  children,
  sortColumn,
  sortDirection,
  onSort,
  title,
}: {
  column: string;
  children: React.ReactNode;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  title?: string;
}) {
  const isActive = sortColumn === column;
  return (
    <th
      className="px-3 py-2 cursor-pointer hover:bg-white/5 transition-colors select-none"
      onClick={() => onSort(column)}
      title={title}
    >
      <div className="flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-[10px]">
            {sortDirection === 'asc' ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </span>
        )}
      </div>
    </th>
  );
}

export function BotTradesBook({
  rows,
  emptyHint = "No closed bot trades yet.",
  paperMode = false,
}: {
  rows: (BotTradeRow & { mint?: string; symbol?: string; imageUri?: string })[];
  emptyHint?: string;
  /** Paper trading: show bonding-curve SOL estimate columns (uses Live entry size from chart panel). */
  paperMode?: boolean;
}) {
  const { solPrice } = useApp();
  const [, setTick] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortColumn) return [...rows].reverse();
    
    const sorted = [...rows].sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case 'token':
          const symbolA = (a as any).symbol || '—';
          const symbolB = (b as any).symbol || '—';
          comparison = symbolA.localeCompare(symbolB);
          break;
        case 'time':
          comparison = a.closedAtTs - b.closedAtTs;
          break;
        case 'age':
          comparison = a.closedAtTs - b.closedAtTs;
          break;
        case 'hold':
          const holdA = a.entryTs ? a.closedAtTs - a.entryTs : 0;
          const holdB = b.entryTs ? b.closedAtTs - b.entryTs : 0;
          comparison = holdA - holdB;
          break;
        case 'delta':
          const deltaA = a.kind === 'tape' ? mcMovePct((a as BotTradeRowTape).entryMcUsd, (a as BotTradeRowTape).exitMcUsd) : 0;
          const deltaB = b.kind === 'tape' ? mcMovePct((b as BotTradeRowTape).entryMcUsd, (b as BotTradeRowTape).exitMcUsd) : 0;
          comparison = deltaA - deltaB;
          break;
        case 'entry':
          const entryA = a.kind === 'tape' ? (a as BotTradeRowTape).entryMcUsd : 0;
          const entryB = b.kind === 'tape' ? (b as BotTradeRowTape).entryMcUsd : 0;
          comparison = entryA - entryB;
          break;
        case 'exit':
          const exitA = a.kind === 'tape' ? (a as BotTradeRowTape).exitMcUsd : 0;
          const exitB = b.kind === 'tape' ? (b as BotTradeRowTape).exitMcUsd : 0;
          comparison = exitA - exitB;
          break;
        case 'eroi':
          const eroiA = (a.kind === 'tape' && (a as BotTradeRowTape).paperSolEstimate?.roiPct) ?? 0;
          const eroiB = (b.kind === 'tape' && (b as BotTradeRowTape).paperSolEstimate?.roiPct) ?? 0;
          comparison = (eroiA as number) - (eroiB as number);
          break;
        case 'enet':
          const enetA = (a.kind === 'tape' && (a as BotTradeRowTape).paperSolEstimate?.netSol) ?? 0;
          const enetB = (b.kind === 'tape' && (b as BotTradeRowTape).paperSolEstimate?.netSol) ?? 0;
          comparison = (enetA as number) - (enetB as number);
          break;
        case 'out':
          comparison = a.exitReason.localeCompare(b.exitReason);
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [rows, sortColumn, sortDirection]);


  const newestFirst = sortedRows;

  const variant = rows.length > 0 && rows[0]!.kind === "chain" ? "chain" : "tape";

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
         <p className="text-[11px] uppercase tracking-widest text-[var(--color-fg-dim)]/30 font-black">
          {emptyHint}
        </p>
      </div>
    );
  }

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: newestFirst.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });

  return (
    <div
      className="overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a] font-mono shadow-lg flex flex-col h-[600px] max-h-[70vh]"
    >
      <table className="w-full border-collapse text-left shrink-0">
        <thead className="bg-[#111111] border-b border-white/10 table w-full table-fixed">
          {variant === "chain" ? (
            <tr className="text-[10px] font-black uppercase tracking-widest text-white/40">
              <SortableHeader column="age" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Age</SortableHeader>
              <SortableHeader column="hold" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} title="Hold time">Hold</SortableHeader>
              <SortableHeader column="roi" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} title="ROI %">ROI</SortableHeader>
              <SortableHeader column="paid" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Paid</SortableHeader>
              <SortableHeader column="back" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Back</SortableHeader>
              <SortableHeader column="net" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Net</SortableHeader>
              <SortableHeader column="out" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Out</SortableHeader>
              <th className="px-3 py-2">Tx</th>
            </tr>
          ) : (
            <tr className="text-[10px] font-black uppercase tracking-widest text-white/40">
              <SortableHeader column="token" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Token</SortableHeader>
              <SortableHeader column="time" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Time</SortableHeader>
              <SortableHeader column="age" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Age</SortableHeader>
              <SortableHeader column="hold" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} title="Hold time">Hold</SortableHeader>
              <SortableHeader column="delta" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} title="MC Move">Δ%</SortableHeader>
              <SortableHeader column="entry" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Entry</SortableHeader>
              <SortableHeader column="exit" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Exit</SortableHeader>
              {paperMode ? (
                <>
                  <SortableHeader column="eroi" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} title="Est ROI">E.ROI</SortableHeader>
                  <SortableHeader column="enet" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>E.Net</SortableHeader>
                </>
              ) : null}
              <SortableHeader column="out" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Out</SortableHeader>
            </tr>
          )}
        </thead>
      </table>
      <div ref={parentRef} className="flex-1 overflow-y-auto">
        <table className="w-full border-collapse text-left">
          <tbody className="text-[11px] block relative" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
            {variant === "chain"
              ? rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const t = newestFirst[virtualRow.index] as BotTradeRowChain;
                  const roi = t.roiPct;
                  const holdTime = t.entryTs ? t.closedAtTs - t.entryTs : 0;
                  return (
                    <tr
                      key={t.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`
                      }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors table table-fixed"
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">{formatAge(t.closedAtTs)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">{formatDuration(holdTime)}</td>
                      <td
                        className={
                          "whitespace-nowrap px-3 py-2 font-bold " +
                          (roi >= 0 ? "text-emerald-400" : "text-rose-400")
                        }
                      >
                        {(roi >= 0 ? "+" : "") + roi.toFixed(2)}%
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-rose-300/70">{formatSol(t.solSpent)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-emerald-300/70">{formatSol(t.solReceived)}</td>
                      <td
                        className={
                          "whitespace-nowrap px-3 py-2 font-bold flex flex-col items-end"
                        }
                      >
                        <span className={t.netSol >= 0 ? "text-emerald-400" : "text-rose-400"}>
                          {(t.netSol >= 0 ? "+" : "") + formatSol(t.netSol)}
                        </span>
                        <span className="text-[9px] text-white/40">
                          ${(t.netSol * solPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/50 uppercase text-[9px] font-black">{exitLabel(t)}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex gap-2 text-[9px] font-black uppercase">
                          <a
                            href={SOLSCAN_TX + encodeURIComponent(t.buySignature)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-400/70 hover:text-emerald-400 transition-colors"
                          >
                            Buy
                          </a>
                          <a
                            href={SOLSCAN_TX + encodeURIComponent(t.sellSignature)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-rose-400/70 hover:text-rose-400 transition-colors"
                          >
                            Sell
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })
            : rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const t = newestFirst[virtualRow.index] as BotTradeRowTape;
                  const pct = mcMovePct(t.entryMcUsd, t.exitMcUsd);
                  const est = t.paperSolEstimate;
                  const symbol = (t as any).symbol || "—";
                  const imageUri = (t as any).imageUri;
                  const holdTime = t.entryTs ? t.closedAtTs - t.entryTs : 0;
                  return (
                    <tr
                      key={t.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`
                      }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors table table-fixed"
                    >
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex items-center gap-2">
                          {imageUri ? (
                            <img
                              src={normalizeCoinImageUri(imageUri) || undefined}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover ring-2 ring-white/10"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : null}
                          <span className="text-[11px] font-medium text-white">{symbol}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">{formatTime(t.closedAtTs)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">{formatAge(t.closedAtTs)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/50">{formatDuration(holdTime)}</td>
                      <td
                        className={
                          "whitespace-nowrap px-3 py-2 font-bold " +
                          (pct >= 0 ? "text-emerald-400" : "text-rose-400")
                        }
                      >
                        {(pct >= 0 ? "+" : "") + pct.toFixed(2)}%
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/70">{formatMcUsdBookDetail(t.entryMcUsd)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-white/70">{formatMcUsdBookDetail(t.exitMcUsd)}</td>
                      {paperMode ? (
                        <>
                          <td
                            className={
                              "whitespace-nowrap px-3 py-2 font-bold " +
                              (est
                                ? est.roiPct >= 0
                                  ? "text-cyan-400"
                                  : "text-amber-400"
                                : "text-white/30")
                            }
                          >
                            {est ? (est.roiPct >= 0 ? "+" : "") + est.roiPct.toFixed(2) + "%" : "—"}
                          </td>
                          <td
                            className={
                              "whitespace-nowrap px-3 py-2 " +
                              (est
                                ? est.netSol >= 0
                                  ? "font-bold text-cyan-400"
                                  : "font-bold text-amber-400"
                                : "text-white/30")
                            }
                          >
                            {est ? (est.netSol >= 0 ? "+" : "") + formatSol(est.netSol) : "—"}
                          </td>
                        </>
                      ) : null}
                      <td className="whitespace-nowrap px-3 py-2 text-white/50 uppercase text-[9px] font-black">{exitLabel(t)}</td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/10 px-3 py-2 text-[9px] leading-tight text-white/30 uppercase font-black tracking-widest bg-[#111111]">
        {variant === "chain"
          ? "ROI uses native SOL balance deltas (inc fees). Verify on Solscan."
          : "Tape-driven MC Δ% only."}
      </div>
    </div>
  );
}
